import { useEffect, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { Menu, X } from "lucide-react";
import { site } from "@/config/site";
import { useScroll } from "@/lib/scroll";
import VendraMark from "@/components/VendraMark";

const LINKS = [
  { label: "The difference", href: "#living-frame" },
  { label: "Work", href: "#work" },
  { label: "Pricing", href: "#pricing" },
  { label: "Questions", href: "#faq" },
];

export default function Nav() {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const { scrollTo } = useScroll();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 80);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const go = (href) => {
    setOpen(false);
    setTimeout(() => scrollTo(href), open ? 260 : 0);
  };

  return (
    <>
      <header
        className={`fixed inset-x-0 top-0 z-50 transition-colors duration-500 ${
          scrolled
            ? "border-b border-hairline bg-ground/90 backdrop-blur-md"
            : "on-dark border-b border-transparent bg-gradient-to-b from-ink/70 via-ink/35 to-transparent"
        }`}
        data-testid="site-nav"
      >
        <nav className="mx-auto flex h-[72px] max-w-[1280px] items-center justify-between px-6 sm:px-10">
          <button
            onClick={() => go("#top")}
            className="text-primaryText transition-opacity duration-300 hover:opacity-80"
            data-testid="nav-wordmark"
            aria-label={`${site.studioName}, back to top`}
          >
            <VendraMark className="h-8 w-auto text-current" color="currentColor" />
          </button>

          <div className="hidden items-center gap-10 md:flex">
            {LINKS.map((l) => (
              <button
                key={l.href}
                onClick={() => go(l.href)}
                className="link-wipe text-sm text-mutedText transition-colors duration-300 hover:text-primaryText"
                data-testid={`nav-link-${l.label.toLowerCase().replace(/\s+/g, "-")}`}
              >
                {l.label}
              </button>
            ))}
            <button
              onClick={() => go("#enquiry")}
              className="btn-sweep rounded-full border border-gold/60 px-5 py-2 text-sm text-primaryText transition-colors duration-300 hover:border-gold hover:bg-gold/10"
              data-testid="nav-cta"
            >
              See your property move
            </button>
          </div>

          <button
            className="text-primaryText md:hidden"
            onClick={() => setOpen(true)}
            aria-label="Open menu"
            data-testid="nav-menu-open"
          >
            <Menu size={24} strokeWidth={1.4} />
          </button>
        </nav>
      </header>

      <AnimatePresence>
        {open && (
          <motion.div
            className="on-dark fixed inset-0 z-[60] flex flex-col bg-ink md:hidden"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.35 }}
            data-testid="mobile-menu"
          >
            <div className="flex h-[72px] items-center justify-between px-6">
              <VendraMark className="h-7 w-auto text-primaryText" color="currentColor" />
              <button
                onClick={() => setOpen(false)}
                aria-label="Close menu"
                data-testid="nav-menu-close"
                className="text-primaryText"
              >
                <X size={24} strokeWidth={1.4} />
              </button>
            </div>
            <div className="flex flex-1 flex-col justify-center gap-2 px-8">
              {LINKS.map((l, i) => (
                <motion.button
                  key={l.href}
                  onClick={() => go(l.href)}
                  className="display border-b border-hairline py-5 text-left text-4xl text-primaryText"
                  initial={{ opacity: 0, y: 18 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.08 * i + 0.1, duration: 0.5 }}
                >
                  {l.label}
                </motion.button>
              ))}
            </div>
            <div className="px-8 pb-12">
              <button
                onClick={() => go("#enquiry")}
                className="w-full rounded-full border border-gold/60 py-4 text-sm tracking-wide text-primaryText"
                data-testid="mobile-cta"
              >
                See your property move
              </button>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
}
